import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";
import consumer from "../channels/consumer";
import ParticleEngine from "../lib/digital_twin/particle_engine";
import TreeRenderer from "../lib/digital_twin/tree_renderer";
import HistoricalDataManager from "../lib/digital_twin/historical_data";
import TopologyEditor from "../lib/digital_twin/topology_editor";

export default class extends Controller {
  static targets = [
    "svg",
    "canvas",
    "status",
    "statusDot",
    "statusText",
    "container",
    "fullscreenLogo",
    "tooltip",
    "copyButton",
    "copyIcon",
    "checkIcon",
    "copyTooltip",
    "timeSlider",
    "timeDisplay",
    "liveButton",
    "timeControls",
    "flowsIcon",
    "editButton",
    "editToolbar",
    "saveButton",
    "confirmOverlay",
    "nodeProperties",
    "nodeTypeLabel",
    "nodeTypeIcon",
    "nodeTypeName",
    "nodeDisplayName",
    "junctionFields",
    "nodeNameInput",
    "nodeFeedInInput",
    "nodeConsumptionInput",
    "nameFieldWrapper",
    "nodeEditLink",
    "feedInLabel",
    "consumptionLabel",
    "symmetricToggle",
    "symmetricWrapper",
    "readOnlyPower",
    "readOnlyPowerValue",
    "powerInputs",
    "batteryFields",
    "batteryCapacityInput",
    "batterySocMinInput",
    "batterySocMaxInput",
  ];
  static values = {
    flowsEnabled: { type: Boolean, default: true },
    editMode: { type: Boolean, default: false },
    topologyNodesUrl: String,
    siteEditUrl: String,
    chargePointEditUrlTemplate: String,
    solarArrayEditUrlTemplate: String,
    returnToUrl: String,
    topology: Object,
    actualsUrl: String,
    ascii: String,
    siteId: Number,
    pollInterval: { type: Number, default: 5000 },
    mode: { type: String, default: "live" },
    selectedTimestamp: String,
    baseDate: String,
    isToday: { type: Boolean, default: true },
    i18nWaiting: String,
    i18nUpdated: String,
    i18nError: String,
    i18nCopied: String,
    i18nLive: String,
    i18nRecorded: String,
    i18nRecordedData: String,
    i18nPlanned: String,
    i18nPlannedData: String,
    i18nPolling: String,
    i18nCharging: String,
    i18nThreePhase: String,
    i18nOnePhase: String,
    i18nSoc: String,
    i18nTilt: String,
    i18nNodeTypes: Object,
    i18nDirection: String,
    i18nDirections: Object,
    i18nDirectionNames: Object,
    i18nPanels: String,
    i18nPower: String,
    i18nCurrent: String,
    i18nCompassLabels: Object,
    i18nConnectorStates: Object,
    i18nJunctionName: String,
    i18nMaxFeedIn: String,
    i18nMaxProduction: String,
    i18nMaxConsumption: String,
    i18nHasChildrenWarning: String,
    i18nAddJunctionMenu: String,
    i18nAddSolar: String,
    i18nAddBattery: String,
    i18nMaxCharge: String,
    i18nMaxDischarge: String,
    i18nUnsavedChanges: String,
    i18nSocBoundsError: String,
    solution: Object,
    saveMode: { type: String, default: "api" },
    saveUrl: String,
  };

  connect() {
    this.particleEngine = new ParticleEngine(this);
    this.treeRenderer = new TreeRenderer(this);
    this.historicalData = new HistoricalDataManager(this);
    this.topologyEditor = new TopologyEditor(this);

    this.isHorizontal = localStorage.getItem("digitalTwinHorizontal") === "true";
    this.isDarkMode = document.documentElement.classList.contains("dark");
    this.actuals = null;
    this.darkModeObserver = new MutationObserver(() => {
      const newDarkMode = document.documentElement.classList.contains("dark");
      if (newDarkMode !== this.isDarkMode) {
        this.isDarkMode = newDarkMode;
        this.renderTree();
      }
    });
    this.darkModeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.resizeObserver = new ResizeObserver(() => {
      if (document.fullscreenElement) return;

      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.handleContainerResize();
      }, 100);
    });

    this.initializeVisualization();
    const startedInHistoricalMode = this.historicalData.initialize();
    this.historicalData.syncUrlParams(startedInHistoricalMode);

    if (!startedInHistoricalMode) {
      this.historicalData.updateTimeDisplay(null);
    }

    const container = this.svgTarget?.parentElement;
    if (container) {
      this.resizeObserver.observe(container);
    }

    if (this.modeValue === "live" && this.hasActualsUrlValue && !startedInHistoricalMode && this.isTodayValue) {
      this.fetchActuals();
      this.subscribeToChannel();
    } else if (!this.isTodayValue) {
      this.historicalData.initializeHistoricalDate();
    }

    if (this.saveModeValue === "textarea") {
      this.handleTopologyDataChanged = this.reloadTopologyFromEvent.bind(this);
      document.addEventListener("topology-data:changed", this.handleTopologyDataChanged);
    }
  }

  reloadTopologyFromEvent(event) {
    if (this.topologyEditor.enabled) return;

    const treeData = event.detail.treeData;
    if (!treeData) return;

    this.topologyValue = this.treeDataToTopology(treeData);
    this.initializeVisualization();
  }

  treeDataToTopology(treeData) {
    const nodes = [];
    const edges = [];
    this.buildFlatNodes(treeData.grid, "grid_connection", null, nodes, edges, treeData.children || []);
    return { nodes, edges };
  }

  buildFlatNodes(config, type, parentId, nodes, edges, children) {
    const id = this.generateNodeId(type, config.name || type);
    const node = {
      id,
      type,
      name: config.name || type,
      min_power_kw: config.power_range_kw?.[0] || 0,
      max_power_kw: config.power_range_kw?.[1] || 0,
      parent: parentId,
    };

    if (config.panel_count != null) node.panel_count = config.panel_count;
    if (config.wp_per_panel != null) node.wp_per_panel = config.wp_per_panel;
    if (config.declination != null) node.declination = config.declination;
    if (config.azimuth != null) node.azimuth = config.azimuth;
    if (config.capacity_kwh != null) node.capacity_kwh = config.capacity_kwh;
    if (config.max_charge_kw != null) node.max_charge_kw = config.max_charge_kw;
    if (config.max_discharge_kw != null) node.max_discharge_kw = config.max_discharge_kw;
    if (config.soc_min_percent != null) node.soc_min_percent = config.soc_min_percent;
    if (config.soc_max_percent != null) node.soc_max_percent = config.soc_max_percent;
    if (config.initial_soc_percent != null) node.initial_soc_percent = config.initial_soc_percent;
    if (config.address) node.address = config.address;
    if (config.metadata?.identifier) node.identifier = config.metadata.identifier;
    if (config.metadata?.phases) node.number_of_phases = config.metadata.phases;
    if (config.metadata?.model) node.model = config.metadata.model;

    nodes.push(node);

    if (parentId) {
      edges.push({ from: parentId, to: id, capacity_kw: node.max_power_kw });
    }

    if (children) {
      for (const child of children) {
        this.buildFlatNodes(child, child.type, id, nodes, edges, child.children);
      }
    }
  }

  generateNodeId(type, name) {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const prefixes = {
      grid_connection: "grid",
      junction: "junction",
      charge_point: "cp",
      connector: "conn",
      solar_inverter: "solar_inverter",
      solar_array: "solar_array",
      battery: "battery",
      other_loads: "other_loads",
    };
    return `${prefixes[type] || "node"}_${slug}`;
  }

  disconnect() {
    this.unsubscribeFromChannel();
    this.stopPolling();
    this.particleEngine.stop();
    if (this.handleTopologyDataChanged) {
      document.removeEventListener("topology-data:changed", this.handleTopologyDataChanged);
    }
    if (this.darkModeObserver) {
      this.darkModeObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  handleFullscreenChange() {
    const isFullscreen = !!document.fullscreenElement;
    const container = this.hasContainerTarget ? this.containerTarget : this.element;
    const viewWrapper = this.svgTarget.parentElement;

    if (isFullscreen) {
      container.style.width = "100vw";
      container.style.height = "100vh";
      container.style.padding = "0";
      container.style.borderRadius = "0";
      viewWrapper.style.height = "100vh";
      if (this.hasFullscreenLogoTarget) {
        this.fullscreenLogoTarget.classList.remove("hidden");
      }
    } else {
      container.style.width = "";
      container.style.height = "";
      container.style.padding = "";
      container.style.borderRadius = "";
      viewWrapper.style.height = "";
      if (this.hasFullscreenLogoTarget) {
        this.fullscreenLogoTarget.classList.add("hidden");
      }
    }

    setTimeout(() => {
      this.particleEngine.reset();

      this.setupDimensions();
      this.createSvg();
      this.setupCanvas();
      this.setupZoom();
      this.renderTree();
      this.fitToView();
    }, 100);
  }

  handleContainerResize() {
    this.particleEngine.reset();

    this.setupDimensions();
    this.createSvg();
    this.setupCanvas();
    this.setupZoom();
    this.renderTree();
    this.fitToView();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.particleEngine.stop();
    } else {
      if (!this.particleEngine.animationFrame && this.ctx) {
        this.particleEngine.start();
      }
    }
  }

  initializeVisualization() {
    const topology = this.topologyValue;
    if (!topology || !topology.nodes) return;

    this.nodes = topology.nodes;
    this.edges = topology.edges || [];

    this.setupDimensions();
    this.createSvg();
    this.setupCanvas();
    this.setupZoom();
    this.renderTree();
    this.fitToView();
    this.particleEngine.start();
  }

  setupCanvas() {
    const styles = getComputedStyle(document.documentElement);
    this.particleEngine.updateColors(styles);
    this.chargingColors = {
      smart: styles.getPropertyValue("--color-energy-fill").trim() || "#22c55e",
      direct: styles.getPropertyValue("--color-energy-fill-fast").trim() || "#3b82f6",
    };

    if (!this.hasCanvasTarget) return;

    const canvas = this.canvasTarget;
    const svgRect = this.svgTarget.getBoundingClientRect();
    const width = svgRect.width;
    const height = svgRect.height;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    this.ctx = canvas.getContext("2d");
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);
  }

  setupDimensions() {
    const container = this.svgTarget.parentElement;
    this.containerWidth = container.offsetWidth;
    this.containerHeight = Math.max(400, container.offsetHeight || 400);
    this.nodeWidth = 140;
    this.nodeHeight = 50;
    this.levelHeight = 120;
    this.visibleEdgeHorizontal = 200;
    this.visibleEdgeVertical = 100;

    const maxDepth = Math.max(...this.nodes.map((n) => this.getNodeDepth(n)));
    const verticalLevelSpacing = this.nodeHeight + this.visibleEdgeVertical;
    const verticalGridLineHeight = this.visibleEdgeVertical + 32;
    this.treeHeight = (maxDepth + 1) * verticalLevelSpacing + verticalGridLineHeight + 40;
  }

  getNodeDepth(node) {
    if (!node.parent) return 0;
    const parent = this.nodes.find((n) => n.id === node.parent);
    return parent ? this.getNodeDepth(parent) + 1 : 0;
  }

  createSvg() {
    d3.select(this.svgTarget).selectAll("*").remove();

    const container = this.svgTarget.parentElement;
    this.viewBoxWidth = container.offsetWidth;
    this.viewBoxHeight = container.offsetHeight;

    this.svgElement = d3
      .select(this.svgTarget)
      .attr("viewBox", `0 0 ${this.viewBoxWidth} ${this.viewBoxHeight}`)
      .style("cursor", "grab");

    this.mainGroup = this.svgElement.append("g").attr("class", "main-group");
  }

  setupZoom() {
    this.zoom = d3
      .zoom()
      .scaleExtent([0.2, 3])
      .on("zoom", (event) => {
        this.mainGroup.attr("transform", event.transform);
        this.currentTransform = event.transform;
      });

    this.svgElement.call(this.zoom).on("dblclick.zoom", null);
  }

  fitToView(animated = false) {
    const bounds = this.mainGroup.node().getBBox();
    if (bounds.width === 0 || bounds.height === 0) return;

    const padding = 40;
    const fullWidth = bounds.width + padding * 2;
    const fullHeight = bounds.height + padding * 2;
    const width = this.viewBoxWidth || this.containerWidth;
    const bottomInset = this.hasTimeControlsTarget
      ? Math.max(0, this.svgTarget.getBoundingClientRect().bottom - this.timeControlsTarget.getBoundingClientRect().top)
      : 0;
    const height = (this.viewBoxHeight || this.containerHeight) - bottomInset;

    const scale = Math.min(width / fullWidth, height / fullHeight, 1);

    const translateX = (width - bounds.width * scale) / 2 - bounds.x * scale;
    const translateY = (height - bounds.height * scale) / 2 - bounds.y * scale;

    const transform = d3.zoomIdentity.translate(translateX, translateY).scale(scale);

    if (animated) {
      this.svgElement.transition().duration(400).call(this.zoom.transform, transform);
    } else {
      this.svgElement.call(this.zoom.transform, transform);
    }
    this.currentTransform = transform;
  }

  zoomIn() {
    this.svgElement.transition().duration(300).call(this.zoom.scaleBy, 1.3);
  }

  zoomOut() {
    this.svgElement.transition().duration(300).call(this.zoom.scaleBy, 0.7);
  }

  resetZoom() {
    this.fitToView(true);
  }

  toggleLayout() {
    this.isHorizontal = !this.isHorizontal;
    localStorage.setItem("digitalTwinHorizontal", this.isHorizontal);
    this.particleEngine.reset();
    this.renderTree();
    this.fitToView();
  }

  toggleFullscreen() {
    const container = this.hasContainerTarget ? this.containerTarget : this.element;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }

  toggleFlows() {
    this.flowsEnabledValue = !this.flowsEnabledValue;

    if (this.hasFlowsIconTarget) {
      this.flowsIconTarget.classList.toggle("opacity-40", !this.flowsEnabledValue);
    }

    if (!this.flowsEnabledValue) {
      this.particleEngine.reset();
      this.particleEngine.clearCanvas();
    }
  }

  async copyAscii() {
    if (!this.hasAsciiValue) return;

    try {
      await this.writeToClipboard(this.asciiValue);

      if (this.hasCopyIconTarget && this.hasCheckIconTarget) {
        this.copyIconTarget.classList.add("hidden");
        this.checkIconTarget.classList.remove("hidden");

        if (this.hasCopyTooltipTarget) {
          const originalText = this.copyTooltipTarget.textContent;
          this.copyTooltipTarget.textContent = this.i18nCopiedValue || "Copied!";
          this.copyTooltipTarget.classList.remove("opacity-0", "group-hover:opacity-100");
          this.copyTooltipTarget.classList.add("opacity-100");

          setTimeout(() => {
            this.checkIconTarget.classList.add("hidden");
            this.copyIconTarget.classList.remove("hidden");
            this.copyTooltipTarget.textContent = originalText;
            this.copyTooltipTarget.classList.add("opacity-0", "group-hover:opacity-100");
            this.copyTooltipTarget.classList.remove("opacity-100");
          }, 2000);
        } else {
          setTimeout(() => {
            this.checkIconTarget.classList.add("hidden");
            this.copyIconTarget.classList.remove("hidden");
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  }

  async writeToClipboard(text) {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  renderTree() {
    this.treeRenderer.render();
  }

  subscribeToChannel() {
    if (!this.hasSiteIdValue) {
      this.startPolling();
      return;
    }

    this.unsubscribeFromChannel();
    this.connectionMode = "connecting";

    this.subscription = consumer.subscriptions.create(
      { channel: "DigitalTwinChannel", site_id: this.siteIdValue },
      {
        connected: this.handleConnected.bind(this),
        disconnected: this.handleDisconnected.bind(this),
        rejected: this.handleRejected.bind(this),
        received: this.handleReceived.bind(this),
      },
    );
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  handleConnected() {
    this.connectionMode = "live";
    this.stopPolling();
    this.updateConnectionStatus();
  }

  handleDisconnected() {
    if (this.historicalData.isViewingHistoricalData()) return;

    this.connectionMode = "polling";
    this.startPolling();
    this.updateConnectionStatus();
  }

  handleRejected() {
    if (this.historicalData.isViewingHistoricalData()) return;

    this.connectionMode = "polling";
    this.startPolling();
    this.updateConnectionStatus();
  }

  handleReceived(data) {
    if (this.historicalData.isViewingHistoricalData()) return;

    if (data.type === "actuals_update" && data.data) {
      this.actuals = data.data;
      if (data.ascii) {
        this.asciiValue = data.ascii;
      }
      this.updateStatusLive();
      this.renderTree();
    }
  }

  updateConnectionStatus() {
    if (!this.hasStatusDotTarget) return;

    const isLive = this.connectionMode === "live";
    if (isLive) {
      this.statusDotTarget.className =
        "flex-none rounded-full p-1 text-message-heading-success bg-message-fill-success animate-pulse";
      this.updateStatusLive();
    } else {
      this.statusDotTarget.className =
        "flex-none rounded-full p-1 text-message-heading-success bg-message-fill-success";
    }
  }

  updateStatusLive() {
    if (this.hasStatusDotTarget) {
      this.statusDotTarget.className =
        "flex-none rounded-full p-1 text-message-heading-success bg-message-fill-success animate-pulse";
    }
    if (this.actuals?.timestamp) {
      this.updateStatusSuccess(new Date(this.actuals.timestamp));
    }
  }

  startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(() => this.fetchActuals(), this.pollIntervalValue);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.backoffTimeout) {
      clearTimeout(this.backoffTimeout);
      this.backoffTimeout = null;
    }
  }

  async fetchActuals() {
    if (!this.hasActualsUrlValue) return;
    if (this.historicalData.isViewingHistoricalData()) return;

    try {
      const response = await fetch(this.actualsUrlValue, {
        headers: { Accept: "application/json" },
      });

      if (response.status === 429) {
        this.handleRateLimited();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      this.consecutiveFailures = 0;
      this.actuals = await response.json();
      this.updateStatusSuccess(new Date(this.actuals.timestamp));
      this.renderTree();
    } catch (error) {
      console.error("Failed to fetch actuals:", error);
      this.updateStatusError(error.message);
    }
  }

  scheduleRetry(retryFn, { timeoutProperty, useExponentialBackoff = false } = {}) {
    let delayMs = 5000;

    if (useExponentialBackoff) {
      this.consecutiveFailures = (this.consecutiveFailures || 0) + 1;
      delayMs = Math.min(5000 * Math.pow(2, this.consecutiveFailures - 1), 30000);
    }

    if (this[timeoutProperty]) {
      clearTimeout(this[timeoutProperty]);
    }

    this[timeoutProperty] = setTimeout(() => {
      this[timeoutProperty] = null;
      retryFn();
    }, delayMs);
  }

  handleRateLimited() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }

    this.scheduleRetry(
      () => {
        this.fetchActuals();
        this.startPolling();
      },
      { timeoutProperty: "backoffTimeout", useExponentialBackoff: true },
    );
  }

  updateStatusSuccess(timestamp, { isHistorical = false, isFuture = false } = {}) {
    if (this.hasStatusDotTarget) {
      this.statusDotTarget.className =
        "flex-none rounded-full p-1 text-message-heading-success bg-message-fill-success";
    }
    if (this.hasStatusTextTarget) {
      const isoTime = timestamp.toISOString();
      let label;
      if (isFuture) {
        label = this.i18nPlannedDataValue || "Planning for";
      } else if (isHistorical) {
        label = this.i18nRecordedDataValue || "Data from";
      } else {
        label = this.i18nUpdatedValue || "Updated";
      }
      this.statusTextTarget.innerHTML = `${label} <time datetime="${isoTime}" data-local="time-ago"></time>`;

      if (window.LocalTime) {
        window.LocalTime.run();
      }
    }
  }

  updateStatusError(message) {
    if (this.hasStatusDotTarget) {
      this.statusDotTarget.className = "flex-none rounded-full p-1 text-message-heading-error bg-message-fill-error";
    }
    if (this.hasStatusTextTarget) {
      const errorText = this.i18nErrorValue || "Error fetching data";
      this.statusTextTarget.textContent = `${errorText}: ${message}`;
    }
  }

  switchMode(event) {
    const mode = event.target.dataset.mode;
    this.modeValue = mode;

    if (mode === "live") {
      this.subscribeToChannel();
    } else {
      this.unsubscribeFromChannel();
      this.stopPolling();
    }
  }

  timeSliderChanged() {
    this.historicalData.timeSliderChanged();
  }

  goLive() {
    this.historicalData.goLive();
  }

  toggleEditMode() {
    this.topologyEditor.toggle();
  }

  saveTopology() {
    this.topologyEditor.trySave();
  }

  async confirmSave() {
    this.topologyEditor.hideConfirmation();
    await this.topologyEditor.save();
  }

  cancelSave() {
    this.topologyEditor.hideConfirmation();
  }

  cancelEdit() {
    this.topologyEditor.cancel();
  }

  nodePropertyChanged() {
    this.topologyEditor.handlePropertyChange();
  }

  powerInputFocused() {
    this.topologyEditor.handlePowerInputFocus(true);
  }

  powerInputBlurred() {
    this.topologyEditor.handlePowerInputFocus(false);
  }

  symmetricToggleChanged() {
    this.topologyEditor.handleSymmetricToggle();
  }
}
